import React, { useCallback, useState } from 'react';

import { TextInput as NativeTextInput, View } from 'react-native';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../text/Text';
import TextInputError from './TextInputError';
import { valueExists } from '../../../common/utils/utils';
import { useDebounce, useEventCallback } from '../../../common/utils/hooks';
import { useTheme } from '../../theme';
import { applyBrandFont } from '../../design/typography';
import { useIsOnCard } from '../../design/fieldSurface';

// copied from web/InputNumber. refactor once we start using hooks in native

const TextInputEditInPlaceNew = ({
	value,
	onConfirm,
	field,
	placeholder,
	disabled,
	style = {},
	password,
	error,
	...passThroughProps
}) => {
	const [intermediateValue, setIntermediateValue] = useState(value);
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const isOnCard = useIsOnCard();

	const _onConfirm = useCallback((v) => {
		onConfirm({ [field]: v });
	}, [onConfirm, field]);

	const debouncedRequest = useDebounce(() => {
		_onConfirm(intermediateValue);
	});

	const onChange = useEventCallback((newValue) => {
		setIntermediateValue(newValue);
		debouncedRequest();
	});

	const onBlur = useEventCallback(() => {
		debouncedRequest.cancel();
		if (value !== intermediateValue) {
			_onConfirm(intermediateValue);
		}
	});

	if (disabled && !password) {
		// A disabled TextInput clips long content and cannot be scrolled, so read-only
		// viewers (e.g. drivers reading directions or access hours) render the full text
		// instead. Password fields keep the masked TextInput so the value is never exposed.
		// An empty string shows the placeholder, matching TextInput behavior.
		const hasReadOnlyValue = valueExists(value) && value !== '';
		return (
			<>
				<View style={[styles.input, style, styles.disabledInput]}>
					<Text
						selectable={true}
						style={!hasReadOnlyValue && styles.readOnlyPlaceholder}
					>
						{hasReadOnlyValue ? value : placeholder}
					</Text>
				</View>
				<TextInputError error={error} />
			</>
		);
	}

	return (
		<>
			<NativeTextInput
				{...passThroughProps}
				style={applyBrandFont([
					styles.input,
					isOnCard && styles.inputOnCard,
					style,
					disabled && styles.disabledInput,
				], theme.ui.areBrandFontsLoaded)}
				placeholderTextColor={theme.ui.colors.textSecondary}
				// What to render in the input
				// The react / html spec disallows providing value as null
				// or not providing a value (as input will be uncontrolled)
				value={!valueExists(intermediateValue) ? '' : intermediateValue}
				onChangeText={onChange}
				selectTextOnFocus={true}
				secureTextEntry={password}
				editable={!disabled}
				placeholder={placeholder}
				onBlur={onBlur}
			/>
			<TextInputError error={error} />
		</>
	);
};

const createStyles = ({ ui: { colors, radius, touch, type } }) => ({
	input: {
		// No lineHeight: it shifts single-line input text on iOS.
		fontSize: type.rowText.fontSize,
		fontFamily: type.rowText.fontFamily,
		minHeight: touch.min,
		borderRadius: radius.card,
		backgroundColor: colors.card,
		color: colors.text,
		paddingHorizontal: 14,
		paddingVertical: 11,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		marginBottom: 10,
		marginTop: 5,
	},
	// Inside a Card the card-coloured fill would vanish (design/fieldSurface.js).
	inputOnCard: {
		backgroundColor: colors.canvas,
	},
	// Keep the outline: disabledBg matches the card in Light.
	disabledInput: {
		backgroundColor: colors.disabledBg,
		borderColor: colors.borderStrong,
	},
	readOnlyPlaceholder: {
		color: colors.textSecondary,
	},
});

export default TextInputEditInPlaceNew;
